import { create } from "zustand";
import { montageApi, toTask, type ApiTask } from "./api";
import { DEPT_ID, GROUP_OF, ME_CLIENT, PEOPLE, type Dept, type PersonId } from "./data/people";
import { notify } from "./lib/notify";
import { bindFlushCreate, realTaskId, settle, trackCreate } from "./lib/tempIds";
import { PLACEHOLDER_POSTER } from "./lib/poster";
import { statusFromItems } from "./lib/domain";
import { dateOf, dayInfo, dayName, fromMin, nowMin, timeOf } from "./lib/time";
import { uid } from "./lib/utils";
import type { Busy, FrameDrawing, SubTask, Task } from "./types";

/**
 * Состояние раздела.
 *
 * Переходы — те же, что в сторе прототипа, и применяются к экрану сразу: так
 * сохраняются анимации и мгновенный отклик. Сервер догоняет двумя способами:
 *
 * - действия, у которых в тосте есть «Отменить», уходят, когда тост догорел
 *   (UNDO_MS). «Отменить» просто не даёт запросу уйти — как у письма в Gmail.
 *   Вкладку закрыли раньше — отложенное отправляется при уходе со страницы;
 * - остальные уходят сразу.
 *
 * Ответ сервера заменяет задачу на экране целиком. Ошибка — задачи
 * перечитываются, а причина показывается тостом.
 */

export type Role = "client" | "editor" | "moderator";
export type Variant = "A" | "B" | "C";
export type Route =
  | { name: "board" }
  | { name: "clientCalendar" }
  | { name: "myTasks" }
  | { name: "editorCalendar" }
  | { name: "editorPosts" }
  | { name: "editorTask"; id: string };
/** Что окно постановки знает заранее: клик по ячейке календаря или по теме на доске. */
export type NewTaskPreset = { editor?: PersonId; day?: number; projectId?: string };
/** `back` — после сохранения или отмены вернуться в окно этой публикации. */
export type NewTaskState = { mode: "create"; preset?: NewTaskPreset } | { mode: "reschedule"; taskId: string; back?: boolean } | null;
/** Фильтры доски: направление и монтажёр (в адресе и запомнены, см. Montage). */
export type BoardFilter = { direction: string | null; editor: string | null };
export type Intent = { id: string; mode: "propose" | "upload" } | null;

export interface NewTaskInput {
  /** название темы */
  title: string;
  /** что снимать — общее ТЗ темы */
  description?: string;
  period?: "day" | "week";
  /** адресат свободной темы (только руководителю имеет смысл задавать) */
  groupId?: string | null;
  dept: Dept;
  /** проект, в паблики которого выйдут ролики */
  projectId: string;
  /** тема руководителя, из которой этот день. Пусто — срочное, помимо тем */
  topicId?: string | null;
  /** её название — чтобы карточка показала тему сразу, не дожидаясь сервера */
  topic?: { title: string; period: "day" | "week" } | null;
  /** видео заранее — необязательно, монтажёр добавит их сам */
  items: SubTask[];
  /** Автоматически публиковать Instagram-ролики после финального аппрува. */
  autoPublishInstagram?: boolean;
  creator: PersonId;
  /** исполнитель; пустая строка — свободная тема */
  editor: PersonId;
  day: number;
  estimate: number;
  due: number;
}

export interface DraftInput {
  fileName: string;
  size: number;
  duration: number;
  videoUrl?: string;
  comment?: string;
  /** файл версии уже в хранилище */
  path: string;
  posterPath?: string;
  /** пост картинками: все файлы по порядку (первый — path) */
  images?: { path: string; name: string; size: number; url?: string }[];
}

/** Сколько живёт «Отменить» — столько же, сколько тост с ней. */
export const UNDO_MS = 5000;

interface State {
  /** `null` — в разделе у человека роли нет (например, распространитель) */
  role: Role | null;
  /** экран задачи у монтажёра — выбран вариант A, модалка */
  variant: Variant;
  route: Route;
  tasks: Task[];
  busy: Busy[];
  loaded: boolean;
  loadError: string | null;
  openTaskId: string | null;
  /** какой ролик открытой публикации на экране — для адреса `?task=&item=` */
  openItemId: string | null;
  /** откуда открыли карточку — модалка «вырастает» из этого места (layoutId `${origin}-${id}`) */
  openOrigin: string;
  newTask: NewTaskState;
  dept: Dept | null;
  q: string;
  board: BoardFilter;
  doneExpanded: boolean;
  intent: Intent;

  load: () => Promise<void>;
  refresh: () => Promise<void>;
  setRole: (r: Role) => void;
  setVariant: (v: Variant) => void;
  navigate: (r: Route) => void;
  openTask: (id: string, origin?: string) => void;
  closeTask: () => void;
  setOpenItem: (itemId: string | null) => void;
  openNewTask: (s: NewTaskState) => void;
  setDept: (d: Dept | null) => void;
  setQ: (q: string) => void;
  setBoard: (patch: Partial<BoardFilter>) => void;
  toggleDoneExpanded: () => void;
  setIntent: (i: Intent) => void;

  createTask: (input: NewTaskInput) => string;
  reschedule: (id: string, editor: PersonId, day: number, estimate: number, due: number) => void;
  cancelTask: (id: string) => void;
  restore: (task: Task) => void;
  remind: (id: string) => void;
  accept: (id: string) => void;
  /** монтажёр берёт свободную тему себе */
  take: (id: string) => Promise<void>;
  /** постановщик назначает тему; пустая строка — вернуть в свободные */
  assign: (id: string, editor: PersonId) => Promise<void>;
  /** видео в тему; возвращает id нового видео или `null` при отказе */
  addItem: (id: string, input: { title: string; format: string; brief?: string }) => Promise<string | null>;
  propose: (id: string, day: number, due: number, note: string) => void;
  acceptProposal: (id: string) => void;
  rejectProposal: (id: string) => void;
  /** монтажёр сдаёт версию одного ролика */
  submitDraft: (id: string, itemId: string, d: DraftInput) => void;
  /** постановщик возвращает ролик с правками */
  sendToRework: (id: string, itemId: string, items: { tc: number; text: string; drawing?: FrameDrawing }[]) => void;
  /** второй фильтр: аппрув ролика, после него он уезжает в Telegram */
  approve: (id: string, itemId: string, autoPublishInstagram?: boolean) => void;
  /** Галочка монтажёра «опубликовать после модерации» — «Президент», 23.10.2026. */
  setPublishAfterModeration: (id: string, itemId: string, enabled: boolean) => void;
  /** первый фильтр: модератор смотрит ролик — «норм» или замечания с таймкодами */
  moderate: (id: string, itemId: string, verdict: "ok" | "rework", items?: { tc: number; text: string; drawing?: FrameDrawing }[]) => void;
  /** ссылка на вышедший пост */
  publishLink: (id: string, itemId: string, link: string) => void;
  /** удалить несданное видео из темы совсем */
  removeItem: (id: string, itemId: string) => void;
  /** снять ролик: «выложили, а постановщик говорит — уберите» */
  cancelItem: (id: string, itemId: string, reason: string) => void;
  /** монтажёр отзывает сданную версию — пока её не посмотрели */
  withdraw: (id: string, itemId: string) => Promise<void>;
  editItem: (id: string, itemId: string, body: { title?: string; brief?: string; accountId?: string | null }) => void;
  /** название, описание и (у постановщика) срок — `due` в минутах от понедельника */
  editTask: (id: string, body: { title?: string; description?: string; due?: number }) => void;
  /** закрыть день с обоснованием */
  justify: (id: string, text: string) => void;
  toggleRevision: (id: string, itemId: string, revId: string) => void;
  sendChat: (id: string, from: PersonId, text: string) => void;
}

/** Переход маршрута раздела — роутер сайта; его подставляет страница. */
let routerNavigate: (path: string) => void = () => {};
export function bindRouter(fn: (path: string) => void) {
  routerNavigate = fn;
}

export const MONTAGE_PATHS = {
  board: "/montage",
  clientCalendar: "/montage/calendar",
  myTasks: "/my-tasks",
  editorCalendar: "/my-tasks/calendar",
  editorPosts: "/my-tasks/posts",
} as const;

// ─────────────────────────── отложенная отправка ────────────────────────

interface Pending {
  taskId: string;
  kind: "create" | "cancel" | "other";
  send: (opts?: RequestInit) => Promise<{ task?: ApiTask } | unknown>;
  timer: number;
}

/** Отложенное — по задаче: у каждой своя «Отменить» и свой таймер. */
const pending = new Map<string, Pending>();

async function flush(taskId: string, opts?: RequestInit) {
  const p = pending.get(taskId);
  if (!p) return;
  pending.delete(taskId);
  window.clearTimeout(p.timer);
  const run = (async () => {
    try {
      // Временные id роликов — до ответа: по ним сопоставим с настоящими.
      const tempItems = p.kind === "create" ? (useStore.getState().tasks.find((t) => t.id === taskId)?.items ?? []).map((i) => i.id) : [];
      const res = (await p.send(opts)) as { task?: ApiTask };
      if (res?.task) {
        if (p.kind === "create") {
          const real = toTask(res.task);
          settle(p.taskId, real.id, tempItems.map((temp, n) => ({ temp, real: real.items[n]?.id ?? temp })));
        }
        useStore.getState()._replace(p.taskId, toTask(res.task));
      }
    } catch (error) {
      notify((error as Error).message || "Не получилось сохранить", { icon: "rework" });
      void useStore.getState().refresh();
    }
  })();
  if (p.kind === "create") trackCreate(taskId, run);
  await run;
}
bindFlushCreate((id) => flush(id));

/**
 * Отправить позже. Если по этой же задаче что-то уже ждёт — оно уходит
 * сейчас: переходы одной задачи должны доехать по порядку.
 */
function defer(taskId: string, kind: Pending["kind"], send: Pending["send"]) {
  if (pending.has(taskId)) void flush(taskId);
  pending.set(taskId, { taskId, kind, send, timer: window.setTimeout(() => void flush(taskId), UNDO_MS) });
}

/** «Отменить»: запрос по этой задаче ещё не ушёл — и не уйдёт. */
function dropPending(taskId: string): Pending | null {
  const p = pending.get(taskId);
  if (!p) return null;
  window.clearTimeout(p.timer);
  pending.delete(taskId);
  return p;
}

/** Уход со страницы: отложенное уходит сразу, с keepalive — браузер довезёт его. */
if (typeof window !== "undefined") {
  const leave = () => {
    for (const id of [...pending.keys()]) void flush(id, { keepalive: true });
  };
  window.addEventListener("pagehide", leave);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") leave();
  });
}


/**
 * Сразу на сервер, результат — на экран.
 *
 * Новая задача уходит на сервер через UNDO_MS: до этого у неё временный id
 * `new-…`, и «Напомнить» в эти секунды отвечал «Задача не найдена» (08.10.2026).
 * Теперь действие сперва досылает создание и идёт уже с настоящим id.
 */
async function now(taskId: string, send: (realId: string) => Promise<{ task: ApiTask }>) {
  try {
    const id = await realTaskId(taskId);
    const res = await send(id);
    if (res?.task) useStore.getState()._replace(id, toTask(res.task));
  } catch (error) {
    notify((error as Error).message || "Не получилось сохранить", { icon: "rework" });
    void useStore.getState().refresh();
  }
}

// ─────────────────────────── стор ───────────────────────────────────────

const name = (id: PersonId) => PEOPLE[id].name;

/** Начало окна «Выполнено»: понедельник этой недели. */
const weekFrom = () => dateOf(0);
const weekTo = () => dateOf(13);

/**
 * Справочник (направления, их аккаунты, люди) перечитывается и без перезагрузки
 * страницы (28.10.2026): вкладки, открытые в 12 минут отката edge 27.10, сутки
 * жили без аккаунтов направлений — пропала галочка автопубликации, а «Своя
 * публикация» уходила без аккаунта.
 */
const DIRECTORY_TTL_MS = 5 * 60_000;
let directoryAt = 0;

export const useStore = create<State & { _replace: (id: string, task: Task) => void }>()((set, get) => {
  const patch = (id: string, fn: (t: Task) => Task) => set((s) => ({ tasks: s.tasks.map((t) => (t.id === id ? fn(t) : t)) }));
  const find = (id: string) => get().tasks.find((t) => t.id === id);
  /**
   * Правка одного ролика на экране. Статус постановки после неё пересчитывается
   * тем же правилом, что на сервере: иначе карточка на секунду остаётся не в
   * своей колонке, а потом прыгает.
   */
  const patchItem = (id: string, itemId: string, fn: (i: SubTask) => SubTask) =>
    patch(id, (t) => {
      const items = t.items.map((i) => (i.id === itemId ? fn(i) : i));
      return { ...t, items, status: statusFromItems(items, t) };
    });

  return {
    role: "client",
    variant: "A",
    route: { name: "board" },
    tasks: [],
    busy: [],
    loaded: false,
    loadError: null,
    openTaskId: null,
    openItemId: null,
    openOrigin: "task",
    newTask: null,
    dept: null,
    q: "",
    board: { direction: null, editor: null },
    doneExpanded: false,
    intent: null,

    async load() {
      try {
        const ctx = await montageApi.context();
        directoryAt = Date.now();
        const { tasks, busy } = await montageApi.tasks(weekFrom(), weekTo());
        set({ role: ctx.me.role, tasks, busy, loaded: true, loadError: null });
      } catch (error) {
        set({ loaded: true, loadError: (error as Error).message });
      }
    },

    async refresh() {
      try {
        if (Date.now() - directoryAt > DIRECTORY_TTL_MS) {
          const ctx = await montageApi.context();
          directoryAt = Date.now();
          set({ role: ctx.me.role });
        }
        const { tasks, busy } = await montageApi.tasks(weekFrom(), weekTo());
        // Пока действие по задаче не ушло, на экране остаётся её локальная
        // версия: иначе перечитывание откатило бы карточку назад, а отменённая
        // задача вернулась бы на доску.
        const local = new Map(get().tasks.map((t) => [t.id, t]));
        const merged = tasks
          .filter((t) => pending.get(t.id)?.kind !== "cancel")
          .map((t) => (pending.has(t.id) && local.has(t.id) ? local.get(t.id)! : t));
        const created = get().tasks.filter((t) => pending.get(t.id)?.kind === "create");
        set({ tasks: [...created, ...merged], busy });
      } catch {
        /* следующее перечитывание догонит */
      }
    },

    _replace: (id, task) =>
      set((s) => ({
        tasks: s.tasks.some((t) => t.id === id) ? s.tasks.map((t) => (t.id === id ? { ...task, freshUntil: t.freshUntil } : t)) : [task, ...s.tasks],
        // Новая задача получила настоящий id — открытая модалка должна остаться на ней
        openTaskId: s.openTaskId === id ? task.id : s.openTaskId,
      })),

    setRole: (role) => set({ role }),
    setVariant: () => set({ variant: "A" }),
    navigate: (route) => {
      set({ route, openTaskId: null, openItemId: null });
      const path = MONTAGE_PATHS[route.name as keyof typeof MONTAGE_PATHS];
      if (path) routerNavigate(path);
    },
    openTask: (id, origin = "task") => set((s) => ({ openTaskId: id, openOrigin: origin, openItemId: s.openTaskId === id ? s.openItemId : null })),
    closeTask: () => set({ openTaskId: null, openItemId: null }),
    setOpenItem: (openItemId) => set({ openItemId }),
    openNewTask: (newTask) => set({ newTask }),
    setDept: (dept) => set({ dept }),
    setQ: (q) => set({ q }),
    setBoard: (patch) => set((s) => ({ board: { ...s.board, ...patch } })),
    toggleDoneExpanded: () => set((s) => ({ doneExpanded: !s.doneExpanded })),
    setIntent: (intent) => set({ intent }),

    createTask: (input) => {
      const at = nowMin();
      const id = `new-${uid()}`;
      const task: Task = {
        id,
        divisionId: DEPT_ID[input.dept] ?? "",
        projectId: input.projectId,
        topicId: input.topicId ?? null,
        topic: input.topic ?? null,
        code: "…",
        title: input.title.trim(),
        description: input.description?.trim() ?? "",
        period: input.period ?? "day",
        groupId: input.groupId ?? (input.editor ? GROUP_OF[input.editor] ?? null : GROUP_OF[input.creator] ?? null),
        dept: input.dept,
        creator: input.creator,
        editor: input.editor,
        // Своя тема монтажёра сразу в работе: у самого себя её не «принимают».
        status: !input.editor ? "open" : input.editor === input.creator ? "work" : "wait",
        acceptedAt: input.editor && input.editor === input.creator ? at : undefined,
        items: input.items.map((i) => ({
          ...i,
          brief: i.brief.trim() || "Без доп. информации — всё в исходниках.",
          autoPublishInstagram: Boolean(input.autoPublishInstagram && i.accountId && !i.accountId.startsWith("tt:") && !i.accountId.startsWith("yt:")),
          autoPublishState: input.autoPublishInstagram && i.accountId && !i.accountId.startsWith("tt:") && !i.accountId.startsWith("yt:") ? "waiting" : "off",
          status: "assigned" as const,
          round: 1,
          versions: [],
          revisions: [],
          poster: PLACEHOLDER_POSTER,
        })),
        day: input.day,
        estimate: input.estimate,
        due: input.due,
        createdAt: at,
        chat: [],
        history: [{ at, kind: "created", text: `Публикация от ${name(input.creator)} — ${input.editor ? name(input.editor) : "свободная"}` }],
        poster: PLACEHOLDER_POSTER,
        freshUntil: Date.now() + 2600,
      };
      set((s) => ({ tasks: [task, ...s.tasks] }));
      defer(id, "create", (opts) =>
        montageApi.create(
          {
            divisionId: DEPT_ID[input.dept],
            projectId: input.projectId,
            topicId: input.topicId ?? null,
            editorId: input.editor || null,
            title: input.title.trim(),
            description: input.description?.trim() ?? "",
            period: input.period ?? "day",
            groupId: input.groupId ?? null,
            workDate: dateOf(input.day),
            dueAt: fromMin(input.due),
            estimate: input.estimate,
            items: input.items.map((i) => ({
              title: i.title.trim(),
              format: i.format,
              brief: i.brief.trim(),
              reference: i.reference?.trim() || undefined,
              accountId: i.accountId ?? null,
              autoPublishInstagram: Boolean(input.autoPublishInstagram && i.accountId && !i.accountId.startsWith("tt:") && !i.accountId.startsWith("yt:")),
              files: i.files.filter((f) => f.path).map((f) => ({ path: f.path!, name: f.name, size: f.size, kind: f.kind, duration: f.duration })),
            })),
          },
          opts,
        ),
      );
      return id;
    },

    reschedule: (id, editor, day, estimate, due) => {
      const t = find(id);
      patch(id, (x) => ({
        ...x,
        editor,
        dept: PEOPLE[editor].editorOf.includes(x.dept) ? x.dept : (PEOPLE[editor].editorOf[0] ?? x.dept),
        day,
        estimate,
        due,
        proposal: undefined,
        history: [...x.history, { at: nowMin(), kind: "rescheduled", text: `Перенос: ${editor ? name(editor) : "свободная"}, ${dayName(day, nowMin()).toLowerCase()}` }],
      }));
      if (!t) return;
      const dept = PEOPLE[editor].editorOf.includes(t.dept) ? t.dept : (PEOPLE[editor].editorOf[0] ?? t.dept);
      defer(id, "other", (opts) =>
        montageApi.schedule(id, { divisionId: DEPT_ID[dept] ?? t.divisionId, editorId: editor || null, workDate: dateOf(day), dueAt: fromMin(due), estimate }, opts),
      );
    },

    cancelTask: (id) => {
      set((s) => ({ tasks: s.tasks.filter((t) => t.id !== id), ...(s.openTaskId === id ? { openTaskId: null, openItemId: null } : {}) }));
      // Задача ещё не ушла на сервер — отмена просто не даёт ей уйти
      if (dropPending(id)?.kind === "create") return;
      defer(id, "cancel", (opts) => montageApi.cancel(id, opts));
    },

    restore: (snap) => {
      const dropped = dropPending(snap.id);
      set((s) => ({ tasks: s.tasks.some((t) => t.id === snap.id) ? s.tasks.map((t) => (t.id === snap.id ? snap : t)) : [snap, ...s.tasks] }));
      if (dropped) return;
      // Запрос уже ушёл. Отмену задачи сервер умеет вернуть; остальное — нет,
      // и честнее перечитать, чем показывать состояние, которого нет.
      if (!snap.id.startsWith("new-")) {
        void now(snap.id, () => montageApi.restore(snap.id));
      }
    },

    remind: (id) => {
      const t = find(id);
      patch(id, (x) => ({ ...x, history: [...x.history, { at: nowMin(), kind: "reminded", text: `Напоминание — ${name(x.editor)}` }] }));
      if (t) void now(id, (realId) => montageApi.remind(realId));
    },

    accept: (id) => {
      patch(id, (t) => ({
        ...t,
        status: statusFromItems(t.items, { ...t, acceptedAt: nowMin() }),
        acceptedAt: nowMin(),
        proposal: undefined,
        history: [...t.history, { at: nowMin(), kind: "accepted", text: `${name(t.editor)}: в работе` }],
      }));
      defer(id, "other", (opts) => montageApi.accept(id, opts));
    },

    take: (id) => now(id, (realId) => montageApi.take(realId)),

    assign: (id, editor) => now(id, (realId) => montageApi.assign(realId, editor || null)),

    addItem: async (id, input) => {
      try {
        const res = await montageApi.addItem(id, input);
        useStore.getState()._replace(id, toTask(res.task));
        return res.itemId;
      } catch (error) {
        notify((error as Error).message || "Не получилось добавить видео", { icon: "rework" });
        return null;
      }
    },

    propose: (id, day, due, note) => {
      patch(id, (t) => ({
        ...t,
        proposal: { day, due, note: note.trim(), at: nowMin() },
        history: [...t.history, { at: nowMin(), kind: "proposed", text: `${name(t.editor)} предлагает ${dayInfo(day).abbr.toLowerCase()} ${dayInfo(day).date} до ${timeOf(due)}` }],
      }));
      defer(id, "other", (opts) => montageApi.propose(id, { workDate: dateOf(day), dueAt: fromMin(due), note: note.trim() }, opts));
    },

    acceptProposal: (id) => {
      patch(id, (t) => {
        if (!t.proposal) return t;
        return {
          ...t,
          day: t.proposal.day,
          due: t.proposal.due,
          status: statusFromItems(t.items, { ...t, acceptedAt: nowMin() }),
          acceptedAt: nowMin(),
          proposal: undefined,
          history: [...t.history, { at: nowMin(), kind: "accepted", text: `Новый день согласован — ${name(t.editor)} в работе` }],
        };
      });
      void now(id, (realId) => montageApi.acceptProposal(realId));
    },

    rejectProposal: (id) => {
      patch(id, (t) => ({ ...t, proposal: undefined }));
      void now(id, (realId) => montageApi.rejectProposal(realId));
    },

    submitDraft: (id, itemId, d) => {
      const item = find(id)?.items.find((i) => i.id === itemId);
      const n = (item?.versions.length ?? 0) + 1;
      patchItem(id, itemId, (i) => ({
        ...i,
        status: "uploaded",
        mod: undefined,
        approvedBy: undefined,
        approvedAt: undefined,
        versions: [...i.versions, { n, at: nowMin(), duration: d.duration, fileName: d.fileName, size: d.size, videoUrl: d.videoUrl, images: d.images?.map((x) => ({ url: x.url ?? null, name: x.name })) ?? null, comment: d.comment?.trim() || undefined, poster: d.images?.[0]?.url ?? i.poster }],
        revisions: i.revisions.map((r) => (r.round < i.round ? { ...r, resolved: true } : r)),
      }));
      patch(id, (t) => ({ ...t, history: [...t.history, { at: nowMin(), kind: "submitted", text: `${name(t.editor)}: «${item?.title ?? ""}» — сдана v${n}` }] }));
      defer(id, "other", (opts) =>
        montageApi.submit(id, itemId, {
          path: d.path, posterPath: d.posterPath, fileName: d.fileName, size: d.size, duration: d.duration, comment: d.comment?.trim() || undefined,
          images: d.images?.map(({ path, name, size }) => ({ path, name, size })),
        }, opts),
      );
    },

    sendToRework: (id, itemId, items) => {
      const item = find(id)?.items.find((i) => i.id === itemId);
      const round = item?.versions.length ?? 0;
      patchItem(id, itemId, (i) => ({
        ...i,
        status: "rework",
        round: round + 1,
        approvedBy: undefined,
        approvedAt: undefined,
        revisions: [...i.revisions, ...items.map((it) => ({ id: uid(), tc: it.tc, text: it.text, drawing: it.drawing, round, by: ME_CLIENT }))],
      }));
      patch(id, (t) => ({
        ...t,
        history: [...t.history, { at: nowMin(), kind: "rework", text: `${name(ME_CLIENT)}: «${item?.title ?? ""}» — ${items.length} ${items.length === 1 ? "правка" : items.length < 5 ? "правки" : "правок"} к v${round}` }],
      }));
      defer(id, "other", (opts) => montageApi.rework(id, itemId, items, opts));
    },

    approve: (id, itemId, autoPublishInstagram) => {
      const item = find(id)?.items.find((i) => i.id === itemId);
      const auto = autoPublishInstagram ?? item?.autoPublishInstagram ?? false;
      patchItem(id, itemId, (i) => ({ ...i, status: "approved", approvedBy: ME_CLIENT, approvedAt: nowMin(), autoPublishInstagram: auto, autoPublishState: auto ? "waiting" : "off", autoPublishError: undefined }));
      patch(id, (t) => ({ ...t, history: [...t.history, { at: nowMin(), kind: "approved", text: `${name(ME_CLIENT)}: «${item?.title ?? ""}» — принято, ушло в Telegram` }] }));
      defer(id, "other", (opts) => montageApi.approve(id, itemId, auto, opts));
    },

    setPublishAfterModeration: (id, itemId, enabled) => {
      patchItem(id, itemId, (i) => ({ ...i, autoPublishInstagram: enabled, autoPublishState: enabled ? "waiting" : "off", autoPublishError: undefined }));
      void now(id, (realId) => montageApi.publishAfterModeration(realId, itemId, enabled));
    },

    moderate: (id, itemId, verdict, items = []) => {
      const item = find(id)?.items.find((i) => i.id === itemId);
      const latest = item?.versions[item.versions.length - 1];
      if (!item || !latest) return;
      const n = latest.n;
      const who = PEOPLE[ME_CLIENT].name;
      patchItem(id, itemId, (i) => {
        const versions = i.versions.map((v) => (v.n === n ? { ...v, mod: { verdict, by: ME_CLIENT, at: nowMin() } } : v));
        if (verdict === "ok") return { ...i, versions, status: "moderated", mod: { verdict, by: ME_CLIENT, at: nowMin() } };
        return {
          ...i,
          versions,
          status: "rework",
          round: i.status === "rework" ? i.round : n + 1,
          mod: { verdict, by: ME_CLIENT, at: nowMin() },
          approvedBy: undefined,
          approvedAt: undefined,
          link: undefined,
          publishedBy: undefined,
          publishedAt: undefined,
          revisions: [...i.revisions, ...items.map((it) => ({ id: uid(), tc: it.tc, text: it.text, drawing: it.drawing, round: n, by: ME_CLIENT }))],
        };
      });
      patch(id, (t) => ({
        ...t,
        history: [...t.history, {
          at: nowMin(),
          kind: "moderated",
          text: verdict === "ok"
            ? `Модератор ${who}: «${item.title}» v${n} — норм`
            : `Модератор ${who}: «${item.title}» — ${items.length} ${items.length === 1 ? "замечание" : items.length < 5 ? "замечания" : "замечаний"} к v${n}`,
        }],
      }));
      defer(id, "other", (opts) => montageApi.moderate(id, itemId, { verdict, versionId: latest.id, items }, opts));
    },

    publishLink: (id, itemId, link) => {
      const item = find(id)?.items.find((i) => i.id === itemId);
      patchItem(id, itemId, (i) => ({ ...i, status: "published", link, publishedBy: ME_CLIENT, publishedAt: nowMin() }));
      patch(id, (t) => ({ ...t, history: [...t.history, { at: nowMin(), kind: "published", text: `${name(ME_CLIENT)}: «${item?.title ?? ""}» — опубликовано, ссылка приложена` }] }));
      void now(id, (realId) => montageApi.publishLink(realId, itemId, link));
    },

    removeItem: (id, itemId) => {
      patch(id, (t) => {
        const items = t.items.filter((i) => i.id !== itemId);
        return { ...t, items, status: statusFromItems(items, t) };
      });
      void now(id, (realId) => montageApi.removeItem(realId, itemId));
    },

    cancelItem: (id, itemId, reason) => {
      const item = find(id)?.items.find((i) => i.id === itemId);
      patchItem(id, itemId, (i) => ({ ...i, status: "cancelled", link: undefined, publishedBy: undefined, publishedAt: undefined }));
      patch(id, (t) => ({
        ...t,
        history: [...t.history, { at: nowMin(), kind: "cancelledItem", text: `${name(ME_CLIENT)}: «${item?.title ?? ""}» — снято${reason.trim() ? ` — ${reason.trim()}` : ""}` }],
      }));
      // С «Отменить» в тосте: запрос уходит, когда тост догорел (как отмена темы).
      defer(id, "other", async (opts) => montageApi.cancelItem(await realTaskId(id), itemId, reason, opts));
    },

    withdraw: (id, itemId) => now(id, (realId) => montageApi.withdraw(realId, itemId)),

    editItem: (id, itemId, body) => {
      patchItem(id, itemId, (i) => ({ ...i, ...(body.title !== undefined ? { title: body.title } : {}), ...(body.brief !== undefined ? { brief: body.brief } : {}), ...(body.accountId !== undefined ? { accountId: body.accountId } : {}) }));
      void now(id, (realId) => montageApi.editItem(realId, itemId, body));
    },

    editTask: (id, body) => {
      patch(id, (t) => ({
        ...t,
        ...(body.title !== undefined ? { title: body.title } : {}),
        ...(body.description !== undefined ? { description: body.description } : {}),
        ...(body.due !== undefined ? { due: body.due, day: Math.floor(body.due / 1440) } : {}),
      }));
      const { due, ...rest } = body;
      void now(id, (realId) => montageApi.editTask(realId, due === undefined ? rest : { ...rest, workDate: dateOf(Math.floor(due / 1440)), dueAt: fromMin(due) }));
    },

    justify: (id, text) => {
      patch(id, (t) => {
        const left = t.items.filter((i) => i.status !== "cancelled" && i.status !== "published").length;
        return {
          ...t,
          justification: text.trim(),
          justifiedAt: nowMin(),
          status: statusFromItems(t.items, { ...t, justification: text.trim() }),
          history: [...t.history, { at: nowMin(), kind: "justified", text: `${name(ME_CLIENT)}: день закрыт, ${left} ${left === 1 ? "ролик не вышел" : left < 5 ? "ролика не вышли" : "роликов не вышли"}` }],
        };
      });
      void now(id, (realId) => montageApi.justify(realId, text.trim()));
    },

    toggleRevision: (id, itemId, revId) => {
      const rev = find(id)?.items.find((i) => i.id === itemId)?.revisions.find((r) => r.id === revId);
      patchItem(id, itemId, (i) => ({ ...i, revisions: i.revisions.map((r) => (r.id === revId ? { ...r, resolved: !r.resolved } : r)) }));
      if (rev) void now(id, () => montageApi.toggleRevision(revId, !rev.resolved));
    },

    sendChat: (id, from, text) => {
      patch(id, (t) => ({ ...t, chat: [...t.chat, { id: uid(), from, text: text.trim(), at: nowMin() }] }));
      void now(id, (realId) => montageApi.message(realId, text.trim()));
    },
  };
});

/**
 * Довезти действия с «Отменить» до выхода из аккаунта. Иначе их таймер
 * срабатывал уже в следующей сессии и запрос уходил с токеном другого
 * сотрудника.
 */
export async function flushMontagePending(): Promise<void> {
  await Promise.all([...pending.keys()].map((id) => flush(id)));
}

/** Не оставлять доску и выбранную карточку предыдущего сотрудника. */
export function resetMontageStore(): void {
  for (const p of pending.values()) window.clearTimeout(p.timer);
  pending.clear();
  useStore.setState({
    role: "client",
    route: { name: "board" },
    tasks: [],
    busy: [],
    loaded: false,
    loadError: null,
    openTaskId: null,
    openItemId: null,
    openOrigin: "task",
    newTask: null,
    dept: null,
    q: "",
    board: { direction: null, editor: null },
    doneExpanded: false,
    intent: null,
  });
}

export const useTask = (id: string | null | undefined) => useStore((s) => (id ? s.tasks.find((t) => t.id === id) : undefined));

/**
 * Скроет ли доска новую публикацию: поиск, направление или монтажёр не те.
 * Раньше после постановки фильтры молча сбрасывались (02.11.2026: человек
 * терял свою выборку) — теперь остаются, а тост предлагает «Показать».
 */
export function boardHides(t: { title: string; projectId: string | null; editor: PersonId }): boolean {
  const { q, board } = useStore.getState();
  if (q && !t.title.toLowerCase().includes(q.toLowerCase())) return true;
  if (board.direction && t.projectId !== board.direction) return true;
  if (board.editor && t.editor !== board.editor) return true;
  return false;
}

/** «Показать» из тоста: снять поиск и фильтры доски. */
export function clearBoardFilters(): void {
  useStore.setState({ q: "", board: { direction: null, editor: null } });
}
